import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '../../../context/NavigationContext';
import useRequest from 'hooks/useRequest';
import { 
  BringzzPageContainer, 
  BringzzIcon, 
  BringzzText, 
  BringzzImage, 
  BringzzButton, 
  BringzzTabs, 
  BringzzTab, 
  BringzzTabPanel, 
  BringzzPostingCard, 
  BringzzInput,
  BringzzMap,
  BringzzBottomDrawer,
  BringzzRadioList,
  BringzzRadio
} from "@bringzz/components";
import AvatarExample from 'assets/images/AvatarExample.png';
import BringzzLogo from 'assets/BringzzLogo.png';
import PostExample1 from 'assets/images/PostExample1.png';

import { useAuth } from 'context/AuthContext';
import { useBookmark } from 'context/BookmarkContext';
import { toast } from 'react-toastify';
import {
  PhotosTab,
  PostsTab,
  TeamTab,
  InfoTab,
  ProductsTab
} from './components';
import { ShimmerAvatar, ShimmerText, ShimmerImage } from './components/Shimmer';
import LetterAvatar from './components/LetterAvatar';
import BioText from './components/BioText';
import PublicOverlay from './components/PublicOverlay';
import BookmarkCollectionDrawer from '../../../components/BookmarkCollectionDrawer';

function PharmacyProfile() {
  const { integrationId } = useParams();
  const { t } = useTranslation('pharmacyProfile');
  const { sendRequest } = useRequest();
  const [pharmacyData, setPharmacyData] = useState(null);
  const [nightDuties, setNightDuties] = useState(null);
  const [loading, setLoading] = useState({
    profile: true,
    followers: true,
    nightDuties: true
  });
  const [errors, setErrors] = useState({
    profile: null,
    followers: null,
    nightDuties: null
  });
  const mapRef = useRef(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const { backPress, navigate } = useNavigation();
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({
    name: '',
    atu: '',
    bio: '',
    coverPicture: null,
    profilePicture: null,
    pharmacyPics: [],
    removedPics: []
  });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const { user } = useAuth();
  const { collections, createCollection } = useBookmark();
  
  const isOwner = user && pharmacyData?.owner && user.id === pharmacyData.owner.id;
  
  // Bookmark states
  const [showBookmarkDrawer, setShowBookmarkDrawer] = useState(false);
  
  const [bookmarkStatus, setBookmarkStatus] = useState('error');

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const profileRes = await sendRequest({
          method: 'GET',
          url: `/module/pharmacy/getPharmacyProfileByIntegrationId?integrationId=${integrationId}`,
        });

        if (profileRes?.status === 'error') {
          // If pharmacy not found and user not logged in, redirect immediately
          if (!user) {
            window.location.href = '/auth';
            return;
          }
          setErrors(prev => ({ ...prev, profile: profileRes.message }));
          setPharmacyData(null);
        } else if (profileRes?.data) {
          setPharmacyData({
            ...profileRes.data,
            products: Array.isArray(profileRes.data.products) ? profileRes.data.products : [],
            team: Array.isArray(profileRes.data.team) ? profileRes.data.team.map(member => ({
              ...member,
              team_details: {
                ...member.team_details,
                userPic: member.team_details?.userPic || AvatarExample,
              },
            })) : []
          });
          
          setBookmarkStatus(profileRes.data.pharmacy?.isBookmarked ? 'success' : 'error');
          setFollowerCount(profileRes.data.pharmacy.followerCount || 0);
          setIsFollowing(Boolean(profileRes.data.pharmacy.isFollowing));
        }
      } catch (err) {
        // If pharmacy not found and user not logged in, redirect immediately
        if (!user) {
          window.location.href = '/auth';
          return;
        }
        setErrors(prev => ({ ...prev, profile: err.message || 'Failed to fetch pharmacy data' }));
        setPharmacyData(null);
      } finally {
        setLoading({ profile: false, followers: false, nightDuties: false });
      }
    };

    fetchInitialData();
  }, [integrationId, sendRequest, user]);
  
  const handleFollow = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    const newIsFollowing = !isFollowing;
    try {
      setIsFollowing(newIsFollowing);
      setFollowerCount(prev => newIsFollowing ? prev + 1 : prev - 1);

      const endpoint = newIsFollowing ? 'followPharmacy' : 'unfollowPharmacy';
      await sendRequest({
        method: 'POST',
        url: `/module/follower/${endpoint}`,
        data: { integration_id: integrationId },
      });

    } catch (err) {
      setIsFollowing(prev => !prev);
      setFollowerCount(prev => newIsFollowing ? prev - 1 : prev + 1);
      console.error('Follow action failed:', err);
    }
  };

  const handleChat = () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!pharmacyData?.owner?.id) {
      toast.error('Unable to start chat: Pharmacy owner not found');
      return;
    }

    navigate(`/settings/chats/${pharmacyData.owner.id}`);
  };

  const handleShare = async () => {
    try {
      const currentUrl = new URL(window.location.href);
      const publicUrl = `${currentUrl.origin}/pharmacy/profile/public/${integrationId}`;
      await navigator.clipboard.writeText(publicUrl);
      toast.success(t('success.link_copied'));
    } catch (err) {
      toast.error(t('errors.copy_link_failed'));
    }
  };
  
  const handleBookmark = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowBookmarkDrawer(true);
  };
  
  const addBookmarkToCollection = async (collectionUuid = null) => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/bookmark/addBookmark',
        data: {
          type: 2,
          elementUUID: integrationId,
          ...(collectionUuid && { collectionUUID: collectionUuid })
        }
      });
      
      if (response.status === 'success') {
        setBookmarkStatus('success');
        setShowBookmarkDrawer(false);
        const bookmarkUrl = collectionUuid ? `/settings/bookmarks/collection?id=${collectionUuid}&tab=pharmacies` : '/settings/bookmarks';
        toast.success(
          <div>
            {t('component.bookmarks.bookmark_success')}{' '}
            <span 
              onClick={() => navigate(bookmarkUrl)}
              className="underline cursor-pointer text-blue-600 hover:text-blue-800"
            >
              View bookmarks
            </span>
          </div>
        );
      } else {
        toast.error(t('component.bookmarks.bookmark_error'));
      }
    } catch (error) {
      toast.error(t('component.bookmarks.bookmark_error'));
    }
  };
  
  const handleRemoveBookmark = async () => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: '/module/bookmark/removeBookmark',
        data: {
          elementUUID: integrationId,
          type: 2
        }
      });
      
      if (response.status === 'success') {
        setBookmarkStatus('error');
        toast.success(t('success.bookmark_removed'));
      } else {
        toast.error(t('errors.bookmark_remove_failed'));
      }
    } catch (error) {
      toast.error(t('errors.bookmark_remove_failed'));
    }
  };
  
  const createNewCollection = async (name) => {
    const success = await createCollection(name);
    if (success) {
      toast.success(t('component.bookmarks.collection_created'));
    } else {
      toast.error(t('component.bookmarks.bookmark_error'));
    }
  };

  const handleEditStart = (field, currentValue) => {
    setEditingField(field);
    setEditValues(prev => ({
      ...prev,
      [field]: currentValue
    }));
  };

  const handleEditCancel = () => {
    setEditingField(null);
    setEditValues({
      name: '',
      atu: '',
      bio: '',
      coverPicture: null,
      profilePicture: null,
      pharmacyPics: [],
      removedPics: []
    });
  };

  const MAX_FILE_SIZE = 2 * 1024 * 1024;

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`${field} ${t('errors.file_size_exceeded')}`);
        return;
      }
      setEditValues(prev => ({ 
        ...prev, 
        [field]: file 
      }));
    }
  };

  const handleGalleryChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploading(true);
      setUploadError(null);
      
      const formData = new FormData();
      formData.append('integrationId', integrationId);
      
      files.forEach((file) => {
        formData.append('pharmacyPics[]', file);
      });

      const response = await sendRequest({
        method: 'POST',
        url: '/module/pharmacy/editPharmacy',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response && response.status === 'success') {
        // Update pharmacy data with complete response
        setPharmacyData({
          ...response.data,
          products: Array.isArray(response.data.products) ? response.data.products : [],
          team: Array.isArray(response.data.team) ? response.data.team.map(member => ({
            ...member,
            team_details: {
              ...member.team_details,
              userPic: member.team_details?.userPic || AvatarExample,
            },
          })) : []
        });
        toast.success(t('success.images_added'));
      } else {
        toast.error(response.message || t('errors.upload_failed'));
      }
    } catch (err) {
      console.error('Failed to upload images:', err);
      toast.error(err.message || t('errors.upload_failed'));
      setUploadError(err.message || t('errors.upload_failed'));
    } finally {
      setUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const deleteGalleryImage = async (imageUrl) => {
    try {
      setUploading(true);
      const response = await sendRequest({
        method: 'POST',
        url: '/module/pharmacy/deletePharmacyPic',
        data: {
          integrationId: pharmacyData.pharmacy.integrationId,
          picUrls: [imageUrl],
        },
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 'success') {
        // Update pharmacy data by removing the deleted image
        setPharmacyData(prev => ({
          ...prev,
          pharmacy: {
            ...prev.pharmacy,
            pharmacyPics: prev.pharmacy.pharmacyPics.filter(pic => pic !== imageUrl)
          }
        }));
        toast.success(t('success.image_deleted'));
      } else {
        toast.error(response.message || t('errors.delete_failed'));
      }
    } catch (err) {
      console.error('Error deleting image:', err);
      toast.error(err.message || t('errors.delete_failed'));
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    const imageUrl = pharmacyImages[index];
    if (imageUrl) {
      deleteGalleryImage(imageUrl);
    }
  };

  const handleSaveChanges = async (field) => {
    try {
      setUploading(true);
      setUploadError(null);
      
      const formData = new FormData();
      formData.append('integrationId', integrationId);
      
      if (field === 'name') {
        formData.append('name', editValues.name);
      }
      
      if (field === 'atu') {
        formData.append('atu', editValues.atu);
      }
      
      if (field === 'bio') {
        formData.append('bio', editValues.bio);
      }
      
      if (field === 'coverPicture' && editValues.coverPicture) {
        formData.append('coverPicture', editValues.coverPicture);
      }
      
      if (field === 'profilePicture' && editValues.profilePicture) {
        formData.append('profilePicture', editValues.profilePicture);
      }
      
      if (field === 'gallery') {
        editValues.pharmacyPics.forEach((file, index) => {
          formData.append(`pharmacyPics[]`, file);
        });
        editValues.removedPics.forEach((url, index) => {
          formData.append(`removedPics[]`, url);
        });
      }

      const response = await sendRequest({
        method: 'POST',
        url: `/module/pharmacy/editPharmacy`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response && response.data) {
        setPharmacyData({
          ...response.data,
          products: Array.isArray(response.data.products) ? response.data.products : [],
          team: Array.isArray(response.data.team) ? response.data.team.map(member => ({
            ...member,
            team_details: {
              ...member.team_details,
              userPic: member.team_details?.userPic || AvatarExample,
            },
          })) : []
        });
        toast.success(`${t('success.updated')} ${field}`);
      } else {
        toast.error(response.message || t('errors.update_failed'));
      }

      setEditingField(null);
      setEditValues({
        name: '',
        atu: '',
        bio: '',
        coverPicture: null,
        profilePicture: null,
        pharmacyPics: [],
        removedPics: []
      });
    } catch (err) {
      console.error('Failed to update pharmacy:', err);
      toast.error(err.message || t('errors.update_failed'));
      setUploadError(err.message || t('errors.update_failed'));
    } finally {
      setUploading(false);
    }
  };

  const pharmacyImages = pharmacyData?.pharmacy?.pharmacyPics || [];
  const displayImages = pharmacyImages;

  const renderLoadingHeader = () => (
    <div className="relative w-full h-full">
      <ShimmerImage className="w-full h-48" />
    </div>
  );

  const renderLoadingAvatar = () => (
    <div className="relative">
      <ShimmerAvatar />
    </div>
  );

  const renderLoadingSubHeader = () => (
    <div className='space-y-6 p-4'>
      <ShimmerText width="w-1/4" />
      <ShimmerText width="w-3/4" height="h-8" />
      <div className='flex justify-between gap-2'>
        <ShimmerText width="w-1/3" height="h-10" />
        <ShimmerText width="w-1/3" height="h-10" />
        <ShimmerText width="w-1/3" height="h-10" />
      </div>
      <div className='space-y-2'>
        <ShimmerText width="w-1/4" />
      </div>
      <div className='flex space-x-4'>
        <ShimmerText width="w-20" />
        <ShimmerText width="w-20" />
      </div>
    </div>
  );

  if (errors.profile && !loading.profile) {
    return (
      <BringzzPageContainer>
        <div className="flex flex-col min-h-screen">
          <div className="relative w-full h-10 z-20 flex justify-between items-center px-4 pt-4">
            <BringzzIcon
              size="24"
              icon="IconChevronLeft"
              folder="LineIcons"
              className="cursor-pointer text-gray-700 hover:text-gray-900 transition-colors"
              onClick={backPress}
            />
          </div>

          <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
            <div className="w-20 h-20 bg-red-500 rounded-full flex items-center justify-center mb-4">
              <BringzzIcon icon="IconClose" folder="LineIcons" size="32" className="text-white" />
            </div>

            <BringzzText tag="h2" className="text-xl font-bold text-gray-900 mb-2 text-center">
              {t('errors.pharmacy_not_found')}
            </BringzzText>
            <BringzzText className="text-sm text-gray-600 text-center mb-6 max-w-xs">
              {t('errors.pharmacy_not_found_description')}
            </BringzzText>

            <div className="flex flex-col gap-2 w-full max-w-xs">
              <BringzzButton
                onClick={() => window.location.reload()}
                className="w-full bg-purple-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm"
              >
                {t('errors.try_again')}
              </BringzzButton>
              <BringzzButton
                onClick={() => navigate('/')}
                className="w-full bg-gray-100 text-gray-700 px-5 py-2.5 rounded-lg font-medium hover:bg-gray-200 transition-colors text-sm"
              >
                {t('errors.go_home')}
              </BringzzButton>
            </div>
          </div>
        </div>
      </BringzzPageContainer>
    );
  }

  if (!pharmacyData && !loading.profile) {
    return null;
  }

  // Only destructure when we have data
  const { pharmacy, owner, opening_hours, team = [], products = [], posts = [] } = pharmacyData || {};

  const daysOfWeek = [
    t('days.monday'), t('days.tuesday'), t('days.wednesday'), t('days.thursday'), 
    t('days.friday'), t('days.saturday'), t('days.sunday')
  ];

  const activeTeamMembers = Array.isArray(team) ? team.filter(member => member?.isActive) : [];
  
  const handleCall = () => {
    window.location.href = `tel:${pharmacy.telefon}`;
  };

  return (
    <>
      <BringzzPageContainer
        className="pb-20 md:pb-0"
        header={{
          left: user ? (
            <BringzzIcon
              size="22"
              icon="IconChevronLeft"
              folder="LineIcons"
              className="cursor-pointer"
              onClick={backPress}
            />
          ) : null,
          right: <>
            {pharmacyData && isOwner && (
              <BringzzIcon
                size="22"
                icon="IconEdit"
                folder="LineIcons"
                className="cursor-pointer mx-2"
              />
            )}
            {pharmacyData && !isOwner && (
              <>
                <BringzzIcon
                  size="22"
                  icon={bookmarkStatus === 'success' ? "IconBookmarkSolid" : "IconBookmark"}
                  folder={bookmarkStatus === 'success' ? "HeroIcons" : "LineIcons"}
                  className="cursor-pointer mx-2"
                  onClick={bookmarkStatus === 'success' ? handleRemoveBookmark : handleBookmark}
                />
                <BringzzIcon
                  size="22"
                  icon="IconUploadAlt"
                  folder="LineIcons"
                  className="cursor-pointer"
                  onClick={handleShare} 
                />
              </>
            )}
          </>,
          background: loading.profile ? renderLoadingHeader() : pharmacyData ? (
            <div className="relative w-full h-full ">
              <BringzzImage 
                className="object-cover w-full h-full" 
                src={
                  editingField === 'coverPicture' && editValues.coverPicture 
                    ? URL.createObjectURL(editValues.coverPicture)
                    : pharmacy?.pharmacyCoverPic || "/images/3.jpg"
                } 
                alt="Pharmacy background" 
              />
              {isOwner && (
                <button 
                  className="absolute top-4 right-4 bg-white rounded-full p-1 shadow-lg z-20 hover:bg-gray-50 transition-colors min-w-[28px] min-h-[28px] flex items-center justify-center"
                  onClick={() => handleEditStart('coverPicture', null)}
                >
                  <BringzzIcon
                    size="16"
                    icon="IconCamera"
                    folder="LineIcons"
                  />
                </button>
              )}
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2">
                <div className="relative">
                  {pharmacy?.pharmacyPic ? (
                    <img 
                      src={pharmacy.pharmacyPic}
                      alt={pharmacy?.name || 'Pharmacy'}
                      className="w-28 h-28 rounded-full object-cover border-4 border-white shadow-lg"
                    />
                  ) : (
                    <LetterAvatar 
                      name={pharmacy?.name || '?'} 
                      size="w-28 h-28"
                      fontSize="text-3xl"
                      className="border-4 border-white shadow-lg"
                    />
                  )}
                  {isOwner && (
                    <label className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-lg z-40 hover:bg-gray-50 transition-colors cursor-pointer">
                      <BringzzIcon
                        size="16"
                        icon="IconCamera"
                        folder="LineIcons"
                      />
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > MAX_FILE_SIZE) {
                              toast.error(t('errors.file_size_exceeded'));
                              return;
                            }
                            
                            try {
                              setUploading(true);
                              const formData = new FormData();
                              formData.append('integrationId', integrationId);
                              formData.append('profilePicture', file);
                              
                              const response = await sendRequest({
                                method: 'POST',
                                url: '/module/pharmacy/editPharmacy',
                                data: formData,
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              if (response?.data) {
                                setPharmacyData({
                                  ...response.data,
                                  products: Array.isArray(response.data.products) ? response.data.products : [],
                                  team: Array.isArray(response.data.team) ? response.data.team.map(member => ({
                                    ...member,
                                    team_details: {
                                      ...member.team_details,
                                      userPic: member.team_details?.userPic || AvatarExample,
                                    },
                                  })) : []
                                });
                                toast.success(t('success.updated'));
                              }
                            } catch (err) {
                              toast.error(err.message || t('errors.update_failed'));
                            } finally {
                              setUploading(false);
                              e.target.value = '';
                            }
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
              {editingField === 'coverPicture' && (
                <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center z-10">
                  <div className="bg-white p-4 rounded-lg w-4/5">
                    <h3 className="font-bold mb-3">{t('header.update_cover_photo')}</h3>
                    <label className="flex items-center justify-center w-full py-2 px-4 bg-magic-lilac rounded-lg text-white cursor-pointer mb-3">
                      <BringzzIcon
                        size="16"
                        icon="IconUploadAlt"
                        folder="LineIcons"
                        className="mr-2"
                      />
                      {t('header.select_photo')}
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'coverPicture')}
                      />
                    </label>
                    <div className="flex justify-end space-x-2">
                      <BringzzButton
                        variant="secondary"
                        onClick={handleEditCancel}
                        disabled={uploading}
                      >
                        {t('header.cancel')}
                      </BringzzButton>
                      <BringzzButton
                        onClick={() => handleSaveChanges('coverPicture')}
                        loading={uploading}
                        disabled={!editValues.coverPicture}
                      >
                        {t('header.save')}
                      </BringzzButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null,

        }}
        subHeader={loading.profile ? renderLoadingSubHeader() : pharmacyData ? (
          <div className='space-y-4 mt-8'>
            {pharmacy?.atu ? (
              <div className="flex items-center">
                {editingField === 'atu' ? (
                  <div className="flex-1">
                    <BringzzInput
                      value={editValues.atu}
                      onChange={(e) => setEditValues(prev => ({ ...prev, atu: e.target.value }))}
                      className="font-bold"
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <BringzzButton
                        variant="secondary"
                        size="sm"
                        onClick={handleEditCancel}
                        disabled={uploading}
                      >
                        {t('header.cancel')}
                      </BringzzButton>
                      <BringzzButton
                        size="sm"
                        onClick={() => handleSaveChanges('atu')}
                        loading={uploading}
                        disabled={!editValues.atu}
                      >
                        {t('header.save')}
                      </BringzzButton>
                    </div>
                  </div>
                ) : (
                  <>
                    <BringzzText tag='p' className="font-medium text-sm text-gray-600">{pharmacy?.atu}</BringzzText>
                    {isOwner && (
                      <button 
                        className="ml-2 text-gray-500 hover:text-gray-700"
                        onClick={() => handleEditStart('atu', pharmacy?.atu)}
                      >
                        <BringzzIcon
                          size="16"
                          icon="IconPencil"
                          folder="LineIcons"
                        />
                      </button>
                    )}
                  </>
                )}
              </div>
            ) : isOwner && (
              <div className="flex items-center">
                {editingField === 'atu' ? (
                  <div className="flex-1">
                    <BringzzInput
                      value={editValues.atu}
                      onChange={(e) => setEditValues(prev => ({ ...prev, atu: e.target.value }))}
                      className="font-bold"
                      placeholder="Add ATU number..."
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <BringzzButton
                        variant="secondary"
                        size="sm"
                        onClick={handleEditCancel}
                        disabled={uploading}
                      >
                        {t('header.cancel')}
                      </BringzzButton>
                      <BringzzButton
                        size="sm"
                        onClick={() => handleSaveChanges('atu')}
                        loading={uploading}
                        disabled={!editValues.atu}
                      >
                        {t('header.save')}
                      </BringzzButton>
                    </div>
                  </div>
                ) : (
                  <button 
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                    onClick={() => handleEditStart('atu', '')}
                  >
                    <BringzzText tag='p' className="text-gray-500">Add ATU</BringzzText>
                  </button>
                )}
              </div>
            )}
            
            <div className="flex items-center">
              {editingField === 'name' ? (
                <div className="flex-1">
                  <BringzzInput
                    value={editValues.name}
                    onChange={(e) => setEditValues(prev => ({ ...prev, name: e.target.value }))}
                    className="text-2xl font-bold"
                  />
                  <div className="flex justify-end space-x-2 mt-2">
                    <BringzzButton
                      variant="secondary"
                      size="sm"
                      onClick={handleEditCancel}
                      disabled={uploading}
                    >
                      {t('header.cancel')}
                    </BringzzButton>
                    <BringzzButton
                      size="sm"
                      onClick={() => handleSaveChanges('name')}
                      loading={uploading}
                      disabled={!editValues.name}
                    >
                      {t('header.save')}
                    </BringzzButton>
                  </div>
                </div>
              ) : (
                <>
                  <BringzzText tag='h2' className="text-2xl">{pharmacy?.name}</BringzzText>
                  {isOwner && (
                    <button 
                      className="ml-2 text-gray-500 hover:text-gray-700"
                      onClick={() => handleEditStart('name', pharmacy?.name)}
                    >
                      <BringzzIcon
                        size="16"
                        icon="IconPencil"
                        folder="LineIcons"
                      />
                    </button>
                  )}
                </>
              )}
            </div>
            
            <div className='flex justify-between gap-2'>
              {!loading.profile && (
                <BringzzButton 
                  className={`${isFollowing ? 'bg-red-500 hover:bg-red-600' : 'bg-magic-lilac hover:bg-magic-lilac-dark'} flex-1 transition-colors`}
                  icon={{ 
                    variant: isFollowing ? "IconMinus" : "IconPlus", 
                    folder: "LineIcons", 
                    size: "16" 
                  }}
                  onClick={handleFollow}
                  disabled={!user}
                >
                  {isFollowing ? t('profile.unfollow') : t('profile.follow')}
                </BringzzButton>
              )}
              <BringzzButton 
                className="border border-black flex-1" 
                icon={{ variant: "IconSpeechBubble16", folder: "LineIcons", size: "18" }}
                onClick={handleChat}
                disabled={!user}
              >
                {t('profile.chat')}
              </BringzzButton>
              <BringzzButton 
                className="border border-black flex-1" 
                icon={{ variant: "IconPhone", folder: "LineIcons", size: "16" }}
                onClick={handleCall}
              >
                {t('profile.phone')}
              </BringzzButton>
            </div>

            {pharmacy?.bio ? (
              <div className="flex items-start">
                {editingField === 'bio' ? (
                  <div className="flex-1">
                    <textarea
                      value={editValues.bio}
                      onChange={(e) => setEditValues(prev => ({ ...prev, bio: e.target.value }))}
                      className="w-full p-2 border rounded-lg resize-none"
                      rows={3}
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <BringzzButton
                        variant="secondary"
                        size="sm"
                        onClick={handleEditCancel}
                        disabled={uploading}
                      >
                        {t('header.cancel')}
                      </BringzzButton>
                      <BringzzButton
                        size="sm"
                        onClick={() => handleSaveChanges('bio')}
                        loading={uploading}
                        disabled={!editValues.bio}
                      >
                        {t('header.save')}
                      </BringzzButton>
                    </div>
                  </div>
                ) : (
                  <>
                    <BioText bio={pharmacy?.bio} />
                    {isOwner && (
                      <button 
                        className="ml-2 text-gray-500 hover:text-gray-700 mt-1"
                        onClick={() => handleEditStart('bio', pharmacy?.bio)}
                      >
                        <BringzzIcon
                          size="16"
                          icon="IconPencil"
                          folder="LineIcons"
                        />
                      </button>
                    )}
                  </>
                )}
              </div>
            ) : isOwner && (
              <div className="flex items-center">
                {editingField === 'bio' ? (
                  <div className="flex-1">
                    <textarea
                      value={editValues.bio}
                      onChange={(e) => setEditValues(prev => ({ ...prev, bio: e.target.value }))}
                      className="w-full p-2 border rounded-lg resize-none"
                      rows={3}
                      placeholder="Write your pharmacy bio..."
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <BringzzButton
                        variant="secondary"
                        size="sm"
                        onClick={handleEditCancel}
                        disabled={uploading}
                      >
                        {t('header.cancel')}
                      </BringzzButton>
                      <BringzzButton
                        size="sm"
                        onClick={() => handleSaveChanges('bio')}
                        loading={uploading}
                        disabled={!editValues.bio}
                      >
                        {t('header.save')}
                      </BringzzButton>
                    </div>
                  </div>
                ) : (
                  <button 
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                    onClick={() => handleEditStart('bio', '')}
                  >
                    <BringzzText tag='p' className="text-gray-500">Add bio</BringzzText>
                  </button>
                )}
              </div>
            )}

            <div className='flex space-x-1 items-center'>
              <BringzzText tag="h5" className="font-normal text-black/40">
                {posts?.length || 0} {t('profile.posts')}
              </BringzzText>
              <p className="text-[5px] text-black/40">&#9679;</p>
              <BringzzText tag="h5" className="font-normal text-black/40">
                {followerCount} {t('profile.followers')}
              </BringzzText>
            </div>
          </div>
        ) : errors.profile ? (
          <div className="p-4">
            <BringzzText tag="p" className="text-red-500">
              {t('errors.profile_load_failed')}
            </BringzzText>
          </div>
        ) : null}
      >
        <BringzzTabs defaultActiveTab="tab1">
          {/* Photos Tab */}
          <BringzzTab id="tab1">
            <BringzzIcon
              size="20"
              icon="IconLayoutAlt1"
              folder="LineIcons"
            />
          </BringzzTab>
          <BringzzTabPanel id="tab1">
            <PhotosTab
              loading={loading.profile}
              error={errors.profile}
              isOwner={isOwner}
              handleGalleryChange={handleGalleryChange}
              uploading={uploading}
              displayImages={displayImages}
              removeGalleryImage={removeGalleryImage}
            />
          </BringzzTabPanel>

          {/* Posts Tab */}
          <BringzzTab id="tab2">
            <BringzzIcon
              size="20"
              icon="IconLayoutAlt"
              folder="LineIcons"
            />
          </BringzzTab>
          <BringzzTabPanel id="tab2">
            <PostsTab
              loading={loading.profile}
              error={errors.profile}
              posts={posts}
              pharmacy={pharmacy}
              user={user}
              collections={collections}
            />
          </BringzzTabPanel>

          {/* Team Tab */}
          <BringzzTab id="tab3">
            <BringzzIcon
              size="20"
              icon="IconUserAlt1"
              folder="LineIcons"
            />
          </BringzzTab>
          <BringzzTabPanel id="tab3">
            <TeamTab
              loading={loading.profile}
              error={errors.profile}
              owner={owner}
              activeTeamMembers={activeTeamMembers}
            />
          </BringzzTabPanel>

          {/* Info Tab */}
          <BringzzTab id="tab4">
            <BringzzIcon
              size="20"
              icon="IconInfoCircle"
              folder="LineIcons"
            />
          </BringzzTab>
          <BringzzTabPanel id="tab4">
            <InfoTab
              loading={loading.profile}
              error={errors.profile}
              pharmacy={pharmacy}
              opening_hours={opening_hours}
              mapRef={mapRef}
            />
          </BringzzTabPanel>

          {/* Products Tab */}
          <BringzzTab id="tab5">
            <BringzzIcon
              size="20"
              icon="IconSearch"
              folder="LineIcons"
            />
          </BringzzTab>
          <BringzzTabPanel id="tab5">
            <ProductsTab
              pharmacyId={pharmacy?.id}
            />
          </BringzzTabPanel>
        </BringzzTabs>

        {showCopiedMessage && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg z-50">
            URL copied to clipboard!
          </div>
        )}
        
        {uploadError && (
          <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
            Error: {uploadError}
          </div>
        )}
        
        <BookmarkCollectionDrawer
          isOpen={showBookmarkDrawer}
          onClose={() => setShowBookmarkDrawer(false)}
          collections={collections}
          onSelectCollection={(collectionId) => addBookmarkToCollection(collectionId)}
          onCreateCollection={createNewCollection}
        />
      </BringzzPageContainer>
      
      {!user && (
        <PublicOverlay 
          onSignUp={() => navigate('/auth')}
          onLogin={() => navigate('/auth')}
          t={t}
        />
      )}
    </>
  );
}

export default PharmacyProfile;